/**
 * Standard Profit & Loss Statement Structure
 *
 * This configuration defines the standardized order and hierarchy for P&L items
 * based on international accounting standards and best practices.
 *
 * Structure follows the standard format:
 * 1. Revenue/Sales (with subcategories)
 * 2. Change in Inventories & Total Output
 * 3. Cost of Goods Sold
 * 4. Gross Profit (calculated)
 * 5. Operating Expenses
 * 6. Operating Income/EBIT
 * 7. Other Income/Expenses
 * 8. Pre-tax Income
 * 9. Taxes
 * 10. Net Income
 */

export interface PLStructureItem {
  key: string;
  label: string;
  level: 0 | 1 | 2;
  isCalculated?: boolean;
  formula?: string;
  subcategories?: string[];
  showGrowthRate?: boolean;
  category?: string;
  aliases?: string[]; // Alternative names this item might appear as
}

export const STANDARD_PL_STRUCTURE: PLStructureItem[] = [
  // REVENUE SECTION
  {
    key: 'sales',
    label: 'Revenue',
    level: 0,
    showGrowthRate: true,
    subcategories: [
      'saas',
      'maintenance and support',
      'hosting and rental',
      'licenses',
      'third-party software',
      'consulting',
      'other revenue',
      'not classified',
      'hardware',
      'subscription revenue',
      'recurring revenue',
      'one-time revenue',
    ],
    aliases: ['revenue', 'income', 'net sales', 'total revenue'],
  },

  // Revenue subcategories (level 1)
  {
    key: 'saas',
    label: 'SaaS Revenue',
    level: 1,
    showGrowthRate: true,
    category: 'sales',
  },
  {
    key: 'licenses',
    label: 'License Revenue',
    level: 1,
    showGrowthRate: true,
    category: 'sales',
  },
  {
    key: 'maintenance and support',
    label: 'Maintenance & Support',
    level: 1,
    showGrowthRate: true,
    category: 'sales',
  },
  {
    key: 'consulting',
    label: 'Consulting Revenue',
    level: 1,
    showGrowthRate: true,
    category: 'sales',
  },
  {
    key: 'other revenue',
    label: 'Other Revenue',
    level: 1,
    showGrowthRate: true,
    category: 'sales',
  },

  // INVENTORY AND OUTPUT ADJUSTMENTS
  {
    key: 'change in inventories',
    label: 'Change in Inventories',
    level: 0,
    aliases: ['inventory changes', 'change in inventory', 'inventory adjustment'],
  },
  {
    key: 'total output',
    label: 'Total Output',
    level: 0,
    aliases: ['gross output', 'total production'],
  },

  // COST OF GOODS SOLD
  {
    key: 'cogs',
    label: 'Cost of Goods Sold',
    level: 0,
    aliases: ['cost of goods sold', 'direct costs', 'cost of sales', 'cost of revenue'],
  },

  // GROSS PROFIT (CALCULATED)
  {
    key: 'gross profit',
    label: 'Gross Profit',
    level: 0,
    isCalculated: true,
    formula: 'sales + change_in_inventories + total_output - cogs',
  },

  // OPERATING EXPENSES
  {
    key: 'opex',
    label: 'Operating Expenses',
    level: 0,
    subcategories: [
      'selling expenses',
      'general and administrative',
      'marketing expenses',
      'research and development',
      'other operating expenses',
    ],
    aliases: ['operating expenses', 'sg&a', 'selling and administrative'],
  },

  // Personnel Expenses
  {
    key: 'pex',
    label: 'Personnel Expenses',
    level: 0,
    subcategories: [
      'admin and finance',
      'consulting',
      'devops',
      'executive management',
      'product management',
      'sales and marketing',
      'support',
    ],
    aliases: ['personnel expenses', 'payroll', 'employee costs', 'staff costs'],
  },

  // OPERATING INCOME
  {
    key: 'ebit',
    label: 'EBIT',
    level: 0,
    isCalculated: true,
    formula: 'gross_profit - opex - pex',
  },

  // OTHER INCOME/EXPENSES
  {
    key: 'other operating income',
    label: 'Other Operating Income',
    level: 0,
    aliases: ['ooi', 'other income'],
  },
  {
    key: 'interest income',
    label: 'Interest Income',
    level: 0,
    aliases: ['financial income'],
  },
  {
    key: 'interest expense',
    label: 'Interest Expense',
    level: 0,
    aliases: ['financial expenses', 'finance costs'],
  },

  // PRE-TAX MEASURES
  {
    key: 'ebitda',
    label: 'EBITDA',
    level: 0,
    isCalculated: true,
    formula: 'ebit + depreciation_and_amortization',
  },
  {
    key: 'ebt',
    label: 'Earnings Before Taxes',
    level: 0,
    aliases: ['earnings before taxes', 'pretax income'],
  },

  // DEPRECIATION & AMORTIZATION
  {
    key: 'depreciation and amortization',
    label: 'Depreciation & Amortization',
    level: 0,
    aliases: ['d&a', 'depreciation of tangible assets', 'amortization of intangible assets'],
  },

  // TAXES
  {
    key: 'tax expense',
    label: 'Tax Expense',
    level: 0,
    aliases: ['income tax', 'taxes', 'tax provision'],
  },

  // NET INCOME
  {
    key: 'net income',
    label: 'Net Income',
    level: 0,
    isCalculated: true,
    formula: 'ebt - tax_expense',
    aliases: ['net profit', 'bottom line', 'profit after tax'],
  },
];

/**
 * Helper function to find a structure item by key or alias
 */
export const findStructureItem = (searchKey: string): PLStructureItem | undefined => {
  const normalizedKey = searchKey.toLowerCase().trim();

  return STANDARD_PL_STRUCTURE.find(item => {
    if (item.key === normalizedKey) return true;
    if (item.aliases?.includes(normalizedKey)) return true;
    return false;
  });
};

/**
 * Get the standard order index for a given P&L item
 */
export const getStandardOrder = (itemKey: string): number => {
  const item = findStructureItem(itemKey);
  if (!item) return 999; // Put unknown items at the end

  return STANDARD_PL_STRUCTURE.findIndex(structItem => structItem.key === item.key);
};

/**
 * Check if an item should show growth rates
 */
export const shouldShowGrowthRate = (itemKey: string): boolean => {
  const item = findStructureItem(itemKey);
  return item?.showGrowthRate || false;
};
